import type { LocationGeocodeResult } from '@/shared/lib/location';
import { Search, X } from 'lucide-react';
import { useState } from 'react';
import { useLocationGeocode } from '@/shared/lib/location';
import { useDebouncedValue } from '@/shared/lib/use-debounced-value';
import { Input } from '@/shared/ui/input';
import { Spinner } from '@/shared/ui/spinner';
import css from './map-search-control.module.scss';

type Props = {
  onSelect: (result: LocationGeocodeResult) => void;
  placeholder?: string;
  // Optional controlled mode: when `value` is provided the host owns the
  // input text (so it can clear it from a shared "reset" affordance).
  // Falls back to internal state when omitted.
  value?: string;
  onValueChange?: (value: string) => void;
};

// Presentational geocode overlay (Nominatim via `useLocationGeocode`).
// Emits the picked result via `onSelect` — it does NOT touch the map, so
// the host decides how to recentre (e.g. feed the result into <FlyTo>).
// Place inside a `position: relative` wrapper over the map, not inside
// <MapContainer> (avoids Leaflet swallowing the input events).
//
// Legacy (V2) clear-X parity (`components/map/map-search.tsx`): the clear
// affordance is a plain × INSIDE the input at the right end, shown ONLY
// after a place is picked from the dropdown — never while the user is still
// typing. Picking a place fills the input with its label and stops
// searching; typing again clears that "selected" state and resumes search.
export const MapSearchControl = ({
  onSelect,
  placeholder = 'Search a place',
  value: controlledValue,
  onValueChange,
}: Props) => {
  const [internalValue, setInternalValue] = useState('');
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;
  const setValue = (next: string) => {
    if (!isControlled) setInternalValue(next);
    onValueChange?.(next);
  };

  // True once a dropdown result is picked; flips back to false the moment
  // the user edits the text again. Drives both the clear-× visibility and
  // the search gate below.
  const [selected, setSelected] = useState(false);

  const debounced = useDebouncedValue(value, 300);
  // Don't re-geocode the label we just filled in after a selection — pass an
  // empty query so the dropdown stays closed (legacy `!isValueSelected`).
  const { data, isFetching } = useLocationGeocode(selected ? '' : debounced);
  const results = selected ? [] : (data ?? []);

  const handleClear = () => {
    setValue('');
    setSelected(false);
  };

  // Plain × inside the input (via the Input's trailing slot), only after a
  // selection. Spinner takes priority while a search is in flight.
  const trailingIcon = isFetching ? (
    <Spinner size="sm" />
  ) : selected ? (
    <button
      aria-label="Clear search"
      className={css.clear}
      onClick={handleClear}
      type="button"
    >
      <X style={{ height: 14, width: 14 }} />
    </button>
  ) : undefined;

  return (
    <div className={css.root}>
      <Input
        aria-label="Search a place"
        leadingIcon={<Search style={{ height: 14, width: 14 }} />}
        onChange={(event) => {
          setValue(event.target.value);
          setSelected(false);
        }}
        placeholder={placeholder}
        trailingIcon={trailingIcon}
        value={value}
      />
      {results.length > 0 && (
        <div className={css.results} role="listbox">
          {results.map((result) => (
            <button
              aria-selected={false}
              className={css.result}
              key={result.id}
              onClick={() => {
                onSelect(result);
                // Legacy: fill the input with the picked label and reveal ×.
                setValue(result.label);
                setSelected(true);
              }}
              role="option"
              type="button"
            >
              {result.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
